import axios, { AxiosInstance } from 'axios';

// Dynamically determine protocol based on current page
const getApiBaseUrl = (): string => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const host = window.location.hostname;
  const port = window.location.protocol === 'https:' ? window.location.port : 8181;
  
  return `${protocol}://${host}${port ? `:${port}` : ''}/api`;
};

const API_BASE_URL = getApiBaseUrl();

class APIClient {
  private client: AxiosInstance;
  private token: string | null = localStorage.getItem('token');

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (this.token) {
      this.setToken(this.token);
    }

    // Add response interceptor for 401 errors (session expired)
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear token and reload to root so the URL is clean after re-login
          this.clearToken();
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
    delete this.client.defaults.headers.common['Authorization'];
  }

  // Auth
  login(password: string) {
    return this.client.post('/auth/login', { password });
  }

  // Servers
  getServers() {
    return this.client.get('/servers');
  }

  getServer(id: string) {
    return this.client.get(`/servers/${id}`);
  }

  createServer(data: any) {
    return this.client.post('/servers', data);
  }

  updateServer(id: string, data: any) {
    return this.client.put(`/servers/${id}`, data);
  }

  deleteServer(id: string) {
    return this.client.delete(`/servers/${id}`);
  }

  deleteServerViaPost(id: string) {
    return this.client.post(`/servers/${id}/delete`);
  }

  launchServer(id: string) {
    return this.client.post(`/servers/${id}/launch`);
  }

  stopServer(id: string) {
    return this.client.post(`/servers/${id}/stop`);
  }

  rotateServerNow(id: string) {
    return this.client.post(`/servers/${id}/rotate-now`);
  }

  syncServerNow(id: string) {
    return this.client.post(`/servers/${id}/sync-now`);
  }

  getServerConfigPath(id: string) {
    return this.client.get(`/servers/${id}/config-path`);
  }

  getServerLogs(id: string, limit?: number) {
    const params = limit ? { limit } : {};
    return this.client.get(`/servers/${id}/logs`, { params });
  }

  clearServerLogs(id: string) {
    return this.client.delete(`/servers/${id}/logs`);
  }

  getPlayerCount(id: string) {
    return this.client.get(`/servers/${id}/player-count`);
  }

  getServerLiveData(id: string) {
    return this.client.get(`/servers/${id}/live-data`);
  }

  getServerEndedLiveData(id: string) {
    return this.client.get(`/servers/${id}/ended-live-data`);
  }

  getResults(id: string, type?: string) {
    const params = type ? { type } : {};
    return this.client.get(`/servers/${id}/results`, { params });
  }

  downloadResult(id: string, filename: string) {
    return this.client.get(`/servers/${id}/results/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    });
  }

  deleteResultFile(id: string, filename: string, deleteFile = false) {
    return this.client.delete(`/servers/${id}/results/${encodeURIComponent(filename)}`, {
      params: { deleteFile },
    });
  }

  refreshResults() {
    return this.client.post('/results/refresh');
  }

  getResultsChampionshipRounds(championshipId: string) {
    return this.client.get(`/results/championships/${championshipId}/rounds`);
  }

  getResultsSession(sessionId: string) {
    return this.client.get(`/results/sessions/${sessionId}`);
  }

  getHotlapFilters() {
    return this.client.get('/results/hotlaps/filters');
  }

  getHotlaps(filters: { championshipId?: string; trackName?: string; trackLayoutName?: string; driverName?: string; carModel?: string; sessionType?: string }) {
    return this.client.get('/results/hotlaps', { params: filters });
  }

  deleteResultSession(sessionId: string, deleteFile = false) {
    return this.client.delete(`/results/sessions/${sessionId}`, {
      params: { deleteFile },
    });
  }

  viewResult(id: string, filename: string) {
    return this.client.get(`/servers/${id}/results/${filename}/view`);
  }

  getPenalties(id: string, filename?: string) {
    const params = filename ? { filename } : {};
    return this.client.get(`/servers/${id}/penalties`, { params });
  }

  addPenalty(id: string, penalty: any) {
    return this.client.post(`/servers/${id}/penalties`, penalty);
  }

  deletePenalty(id: string, penaltyId: string) {
    return this.client.delete(`/servers/${id}/penalties/${penaltyId}`);
  }

  saveServerJson(id: string, config: any) {
    return this.client.put(`/servers/${id}/json/server`, config);
  }

  saveSeasonJson(id: string, config: any) {
    return this.client.put(`/servers/${id}/json/season`, config);
  }

  // Assets
  getCars() {
    return this.client.get('/assets/cars');
  }

  getRaceTracks() {
    return this.client.get('/assets/race-tracks');
  }

  getPracticeTracks() {
    return this.client.get('/assets/practice-tracks');
  }

  // Championships
  getChampionships() {
    return this.client.get('/championships');
  }

  // Website config (admin)
  getWebsiteConfig() {
    return this.client.get('/website-config');
  }

  saveWebsiteConfig(config: any) {
    return this.client.post('/website-config', config);
  }

  createChampionship(data: any) {
    return this.client.post('/championships', data);
  }

  updateChampionship(id: string, data: any) {
    return this.client.put(`/championships/${id}`, data);
  }

  deleteChampionship(id: string) {
    return this.client.delete(`/championships/${id}`);
  }

  getChampionshipEntryList(id: string) {
    return this.client.get(`/championships/${id}/entrylist`);
  }

  saveChampionshipEntryList(id: string, data: { registrations?: any[]; entrylist?: any }) {
    return this.client.post(`/championships/${id}/entrylist`, data);
  }

  debugReplaySnapshots(entries: any[], seasonConfig: any) {
    return this.client.post('/servers/debug/replay-snapshots', { entries, seasonConfig });
  }

  // Discord Bot
  getDiscordBotConfig() {
    return this.client.get('/discord-bot');
  }

  saveDiscordBotConfig(config: any) {
    return this.client.post('/discord-bot', config);
  }

  restartDiscordBot() {
    return this.client.post('/discord-bot/restart');
  }

  getDiscordBotStatus() {
    return this.client.get('/discord-bot/status');
  }

  // SimGrid
  fetchSimgridChampionship(simgridId: number | string) {
    return this.client.get(`/simgrid/championship/${simgridId}`);
  }

  // Championship sign-outs
  getChampionshipSignouts(championshipId: string) {
    return this.client.get(`/championships/${championshipId}/signouts`);
  }

  signOutDriver(championshipId: string, eventId: string, roundId: string, steamId: string, driverName: string) {
    return this.client.post(`/championships/${championshipId}/events/${eventId}/rounds/${roundId}/signouts`, { steamId, driverName });
  }

  signInDriver(championshipId: string, eventId: string, roundId: string, steamId: string) {
    return this.client.delete(`/championships/${championshipId}/events/${eventId}/rounds/${roundId}/signouts/${steamId}`);
  }
}

const api = new APIClient();
export default api;
